import type { ParsedLicense, ParsedAddress } from "./types";

// AAMVA DL/ID payloads open with "@", LF (0x0A), RS (0x1E), CR (0x0D), then
// "ANSI ". Real licenses include the CR segment terminator at offset 3; some
// encoders (and our older fixtures) drop it. Anchor detection on the "@"
// compliance indicator plus the mandatory "ANSI" file-type marker appearing
// near the start, rather than an exact control-byte prefix — an exact prefix
// that omitted the CR rejected every compliant license.
function hasAamvaHeader(payload: string): boolean {
  if (!payload.startsWith("@")) return false;
  const ansiIdx = payload.indexOf("ANSI");
  return ansiIdx >= 2 && ansiIdx <= 5;
}

const D_CODES: Record<string, keyof DCodeMap> = {
  DCS: "lastName",
  DAB: "lastName",
  DAC: "firstName",
  DCT: "firstName",
  DBB: "dobRaw",
  DBC: "sexRaw",
  DAQ: "driverLicenseNo",
  DAJ: "driverLicenseJurisdiction",
  DAG: "addressStreet",
  DAI: "addressCity",
  DAK: "addressPostalCode",
};

interface DCodeMap {
  lastName?: string;
  firstName?: string;
  dobRaw?: string;
  sexRaw?: string;
  driverLicenseNo?: string;
  driverLicenseJurisdiction?: string;
  addressStreet?: string;
  addressCity?: string;
  addressPostalCode?: string;
}

export function parseAamva(payload: string): ParsedLicense | null {
  if (!payload || !hasAamvaHeader(payload)) {
    return null;
  }

  const raw = extractDCodes(payload);
  const result: ParsedLicense = {};

  result.firstName = raw.firstName;
  result.lastName = raw.lastName;
  if (raw.dobRaw) {
    const dob = parseDob(raw.dobRaw);
    if (dob) result.dob = dob;
  }
  if (raw.sexRaw) {
    const sex = parseSex(raw.sexRaw);
    if (sex) result.sex = sex;
  }
  if (raw.driverLicenseNo) result.driverLicenseNo = raw.driverLicenseNo.trim();
  if (raw.driverLicenseJurisdiction) {
    result.driverLicenseJurisdiction = raw.driverLicenseJurisdiction.trim();
  }

  const address = buildAddress(raw);
  if (address) result.address = address;

  return result;
}

function extractDCodes(payload: string): DCodeMap {
  const out: DCodeMap = {};

  // Split by LF, RS and CR; each element either is a "DXX..." token or noise.
  // CR (0x0D) terminates subfiles in real payloads — splitting on it too
  // keeps a subfile boundary from gluing two elements into one token.
  // eslint-disable-next-line no-control-regex
  const tokens = payload.split(/[\n\x1e\r]/g);
  for (let raw of tokens) {
    // The first D-code after a subfile designator (e.g. "DL", "ID") is
    // concatenated directly: "DLDAQO1234..." — strip the 2-char subfile prefix.
    if (raw.length >= 5 && /^[A-Z]{2}D[A-Z]{2}/.test(raw)) {
      raw = raw.slice(2);
    }
    if (raw.length < 4) continue;
    const code = raw.slice(0, 3);
    const value = raw.slice(3).trim();
    if (!value) continue;
    const mapped = D_CODES[code];
    if (!mapped) continue;
    // First non-empty wins: spec allows duplicate codes across older/newer specs.
    if (out[mapped] === undefined) {
      out[mapped] = value;
    }
  }

  return out;
}

function parseDob(raw: string): string | undefined {
  const digits = raw.replace(/\D/g, "");
  if (digits.length !== 8) return undefined;

  // Two valid layouts:
  //   MMDDCCYY (US AAMVA)
  //   CCYYMMDD (Canada post-2014)
  // Disambiguate by checking the first two digits: if 19 or 20, treat as CCYY.
  const first2 = parseInt(digits.slice(0, 2), 10);
  const isCanadianLayout = first2 === 19 || first2 === 20;

  const year = isCanadianLayout ? digits.slice(0, 4) : digits.slice(4, 8);
  const month = isCanadianLayout ? digits.slice(4, 6) : digits.slice(0, 2);
  const day = isCanadianLayout ? digits.slice(6, 8) : digits.slice(2, 4);

  const m = parseInt(month, 10);
  const d = parseInt(day, 10);
  if (m < 1 || m > 12 || d < 1 || d > 31) return undefined;

  return `${year}-${month}-${day}`;
}

function parseSex(raw: string): "M" | "F" | "X" | undefined {
  const trimmed = raw.trim();
  if (trimmed === "1" || trimmed.toUpperCase() === "M") return "M";
  if (trimmed === "2" || trimmed.toUpperCase() === "F") return "F";
  if (trimmed === "9" || trimmed.toUpperCase() === "X") return "X";
  return undefined;
}

function buildAddress(raw: DCodeMap): ParsedAddress | undefined {
  if (!raw.addressStreet || !raw.addressCity) {
    return undefined;
  }
  return {
    street: raw.addressStreet.trim(),
    city: raw.addressCity.trim(),
    province: (raw.driverLicenseJurisdiction ?? "").trim(),
    postalCode: normalizePostalCode((raw.addressPostalCode ?? "").trim()),
  };
}

/**
 * US AAMVA DAK encodes the ZIP+4 as 9 contiguous digits without the
 * conventional hyphen (e.g. "100120000" means ZIP 10012 with no ZIP+4
 * extension). Insert the hyphen so display + geocoding both behave as
 * expected. Canadian postal codes (6 alphanumeric, e.g. "M5V1A1") and
 * any other format pass through unchanged.
 */
function normalizePostalCode(raw: string): string {
  if (/^\d{9}$/.test(raw)) {
    return `${raw.slice(0, 5)}-${raw.slice(5)}`;
  }
  return raw;
}
